import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  AlertTriangle, 
  Trash2, 
  Users, 
  MessageSquare, 
  Database, 
  Shield,
  Loader2,
  Download,
} from 'lucide-react';
import { apiMethods, DataSummary, DeleteResponse } from '../lib/apiClient';

const DataManagement: React.FC = () => {
  const [dataSummary, setDataSummary] = useState<DataSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchDataSummary();
  }, []);

  const fetchDataSummary = async () => {
    try {
      setLoading(true);
      const response = await apiMethods.getDataSummary();
      setDataSummary(response);
    } catch (err) {
      setError('Failed to fetch data summary');
      console.error('Error fetching data summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: 'messages' | 'customers' | 'account') => {
    try {
      setDeleting(type);
      setError(null);
      setSuccess(null);

      let response: DeleteResponse;
      switch (type) {
        case 'messages':
          response = await apiMethods.deleteAllMessages();
          break;
        case 'customers':
          response = await apiMethods.deleteAllCustomers();
          break;
        case 'account':
          response = await apiMethods.deleteAllAccountData();
          break;
        default:
          throw new Error('Invalid deletion type');
      }
      
      if (response.status === 'success') {
        setSuccess(response.message);
        // Refresh data summary
        await fetchDataSummary();
      } else {
        setError('Deletion failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Deletion failed');
      console.error(`Error deleting ${type}:`, err);
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      setSuccess(null);
      const blob = await apiMethods.exportTenantData();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `edyt-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Your data export has been downloaded.');
    } catch (err) {
      setError('Failed to export data. Please try again.');
      console.error('Error exporting data:', err);
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = (type: 'messages' | 'customers' | 'account') => {
    const confirmations = {
      messages: 'Are you sure you want to delete ALL Slack messages? This action cannot be undone.',
      customers: 'Are you sure you want to delete ALL customers? This action cannot be undone.',
      account: 'Are you sure you want to delete ALL your data? This will remove everything except your basic account info. This action cannot be undone.'
    };

    if (window.confirm(confirmations[type])) {
      handleDelete(type);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading data summary...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Summary */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data Summary
        </h3>
        {dataSummary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="text-2xl font-bold">{dataSummary.messages_count}</div>
              <div className="text-sm text-muted-foreground">Messages</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Users className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold">{dataSummary.customers_count}</div>
              <div className="text-sm text-muted-foreground">Accounts</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Shield className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <div className="text-2xl font-bold">{dataSummary.insights_count}</div>
              <div className="text-sm text-muted-foreground">Insights</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Database className="h-8 w-8 mx-auto mb-2 text-orange-500" />
              <div className="text-2xl font-bold">{dataSummary.source_mappings_count}</div>
              <div className="text-sm text-muted-foreground">Source Mappings</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Shield className="h-8 w-8 mx-auto mb-2 text-cyan-500" />
              <div className="text-2xl font-bold">{dataSummary.integrations_count}</div>
              <div className="text-sm text-muted-foreground">Integrations</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Shield className="h-8 w-8 mx-auto mb-2 text-indigo-500" />
              <div className="text-2xl font-bold">{dataSummary.chat_items_count ?? 0}</div>
              <div className="text-sm text-muted-foreground">Chat Items</div>
            </div>
          </div>
        )}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Badge variant="outline" className="text-lg px-4 py-2">
            Total: {dataSummary?.total_data_points || 0} data points
          </Badge>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            className="gap-2"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download my data
          </Button>
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Exports all organization data in machine-readable JSON (GDPR Art. 15/20).
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Delete Actions */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-destructive dark:text-red-400">
          <Trash2 className="h-5 w-5" />
          Data Deletion
        </h3>
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> These actions are irreversible. Please proceed with caution.
            </AlertDescription>
          </Alert>

          {/* Delete Messages */}
          <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg bg-destructive/5 dark:bg-destructive/20 dark:border-destructive/30">
            <div>
              <h4 className="font-semibold text-destructive dark:text-red-400">Delete All Messages</h4>
              <p className="text-sm text-destructive/80 dark:text-red-400/80">
                Remove all Slack messages and events from your workspace
              </p>
              <Badge variant="outline" className="mt-1">
                {dataSummary?.messages_count || 0} messages
              </Badge>
            </div>
            <Button
              variant="destructive"
              onClick={() => confirmDelete('messages')}
              disabled={deleting === 'messages' || (dataSummary?.messages_count || 0) === 0}
            >
              {deleting === 'messages' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Messages
            </Button>
          </div>

          {/* Delete Customers */}
          <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg bg-destructive/5 dark:bg-destructive/20 dark:border-destructive/30">
            <div>
              <h4 className="font-semibold text-destructive dark:text-red-400">Delete All Accounts</h4>
              <p className="text-sm text-destructive/80 dark:text-red-400/80">
                Remove all account profiles and associated insights
              </p>
              <Badge variant="outline" className="mt-1">
                {dataSummary?.customers_count || 0} accounts
              </Badge>
            </div>
            <Button
              variant="destructive"
              onClick={() => confirmDelete('customers')}
              disabled={deleting === 'customers' || (dataSummary?.customers_count || 0) === 0}
            >
              {deleting === 'customers' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Accounts
            </Button>
          </div>

          {/* Delete Account Data */}
          <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg bg-destructive/10 dark:bg-destructive/25 dark:border-destructive/40">
            <div>
              <h4 className="font-semibold text-destructive dark:text-red-400">Delete All Account Data</h4>
              <p className="text-sm text-destructive/80 dark:text-red-400/80">
                Remove all data except basic account information (tenant info, user mapping)
              </p>
              <Badge variant="destructive" className="mt-1">
                {dataSummary?.total_data_points || 0} total data points
              </Badge>
            </div>
            <Button
              variant="destructive"
              onClick={() => confirmDelete('account')}
              disabled={deleting === 'account'}
            >
              {deleting === 'account' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete All Data
            </Button>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> Deleting account data will preserve your basic tenant information 
              and user mapping, but will remove all messages, customers, insights, source mappings, 
              and integration configurations.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;
